import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  fetchEmailTemplates,
  fetchEmailTemplatePlaceholders,
  sendEmailUsingTemplate,
} from "../services/emailTemplateService";

/**
 * Hook to fetch email templates.
 *
 * @param {string} [contextType] - "lead" | "customer". When passed, only
 *   templates tagged for that context are returned, so lead screens don't offer
 *   customer templates and vice-versa. Templates are tagged via the
 *   "Template For" field in Create Email Template (`context_type`).
 *   Omit the argument to get every template (unchanged behaviour).
 */
export const useEmailTemplates = (contextType) => {
  const want = contextType ? String(contextType).trim().toLowerCase() : "";
  return useQuery({
    queryKey: ["emailTemplates", want || "all"],
    // Filtering happens on the server now — no context param means every template.
    queryFn: () => fetchEmailTemplates(want ? { context_type: want } : {}),
    staleTime: 5 * 60 * 1000, // 5 min cache
    refetchOnMount: "always",
    select: (data) => (Array.isArray(data) ? data : data?.data ?? []),
  });
};

/**
 * Hook to fetch placeholder definitions
 */
export const useEmailTemplatePlaceholders = () => {
  return useQuery({
    queryKey: ["emailTemplatePlaceholders"],
    queryFn: fetchEmailTemplatePlaceholders,
    staleTime: 10 * 60 * 1000, // 10 min cache
  });
};

/**
 * Hook to send email using a template
 */
export const useSendEmailUsingTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: sendEmailUsingTemplate,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["leadFollowUps"] });
      toast.success(data?.message || "Email sent successfully!");
    },
    onError: (error) => {
      toast.error(error.message || "Failed to send email");
    },
  });
};